import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Slider, FormGroup, FormControlLabel, Checkbox, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function ValueBadge({ value, sx }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.5,
        background: '#1976d2',
        color: 'white',
        borderRadius: 2,
        fontWeight: 600,
        fontSize: '0.95rem',
        boxShadow: '0 2px 8px 0 rgba(25, 118, 210, 0.10)',
        display: 'inline-block',
        minWidth: 48,
        textAlign: 'center',
        ...sx,
      }}
    >
      {value}
    </Box>
  );
}

const fallbackParsePrice = (price) => {
  const numeric = parseFloat(
    String(price ?? '')
      .replace(/[^\d.,-]/g, '')
      .replace(/,/g, '')
  );
  return Number.isNaN(numeric) ? null : numeric;
};

const fallbackFormatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  const numeric = Number(value);
  return Number.isNaN(numeric) ? '$0.00' : `$${numeric.toFixed(2)}`;
};

function FilterSidebar({
  allResults,
  filters,
  setFilters,
  onCollapse,
  isMobile = false,
  formatCurrency,
  parsePrice,
}) {
  const [stores, setStores] = useState([]);
  const parsePriceValue = useMemo(() => parsePrice || fallbackParsePrice, [parsePrice]);
  const formatPriceValue = useMemo(() => formatCurrency || fallbackFormatCurrency, [formatCurrency]);

  // Extract unique stores and price range from results
  useEffect(() => {
    if (!allResults.length) return;

    const storeSet = new Set();
    let minPrice = Infinity;
    let maxPrice = 0;

    allResults.forEach(item => {
      storeSet.add(item.source);
      const price = parsePriceValue(item.numericPrice ?? item.price);
      if (typeof price === 'number' && !Number.isNaN(price)) {
        minPrice = Math.min(minPrice, price);
        maxPrice = Math.max(maxPrice, price);
      }
    });

    const safeMin = Number.isFinite(minPrice) ? minPrice : 0;
    const safeMax = Number.isFinite(maxPrice) ? maxPrice : 0;

    setStores([...storeSet]);
    setFilters(prev => ({
      ...prev,
      priceRange: [safeMin, safeMax],
      price: safeMax // Set initial price to max
    }));
  }, [allResults, parsePriceValue, setFilters]);

  const handleReset = () => {
    if (!allResults.length) return;

    // Calculate max price from results
    const parsedPrices = allResults
      .map(item => parsePriceValue(item.numericPrice ?? item.price))
      .filter(value => typeof value === 'number' && !Number.isNaN(value));
    const maxPrice = parsedPrices.length ? Math.max(...parsedPrices) : 0;

    setFilters({
      price: maxPrice,
      priceRange: [0, maxPrice],
      rating: 0,
      stores: []
    });
  };

  const minPrice = Number.isFinite(filters.priceRange?.[0]) ? filters.priceRange[0] : 0;
  const maxPrice = Number.isFinite(filters.priceRange?.[1]) ? filters.priceRange[1] : 0;
  const currentPrice = typeof filters.price === 'number' ? filters.price : maxPrice;
  const minRating = 0;
  const maxRating = 5;
  const currentRating = filters.rating;

  return (
    <Box
      sx={{
        width: isMobile ? '100%' : 250,
        p: 2,
        borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
        borderBottom: isMobile ? '1px solid #e0e0e0' : 'none',
        borderRadius: isMobile ? 2 : 0,
        boxShadow: isMobile ? '0 8px 24px rgba(15, 23, 42, 0.08)' : 'none',
        backgroundColor: 'white',
        fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif`
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif`, fontWeight: 700 }}>Filters</Typography>
        <Box>
          <Button
            size="small"
            onClick={handleReset}
            sx={{ mr: onCollapse ? 1 : 0, fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif` }}
          >
            Reset
          </Button>
          {onCollapse && (
            <IconButton size="small" onClick={onCollapse}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Modern Price Slider */}
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif` }}>Price</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif`, minWidth: 64, textAlign: 'left' }}>
            {formatPriceValue(minPrice)}
          </Typography>
          <ValueBadge value={formatPriceValue(currentPrice)} sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif` }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif`, minWidth: 64, textAlign: 'right' }}>
            {formatPriceValue(maxPrice)}
          </Typography>
        </Box>
        <Slider
          value={currentPrice}
          onChange={(_, value) => setFilters(prev => ({ ...prev, price: value }))}
          min={minPrice}
          max={maxPrice}
          valueLabelDisplay="off"
          sx={{ mt: 0, mb: 0.5 }}
        />
      </Box>

      {/* Modern Rating Slider */}
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif` }}>Minimum Rating</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif`, minWidth: 36, textAlign: 'left' }}>{minRating} stars</Typography>
          <ValueBadge value={`${currentRating} stars`} sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif` }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif`, minWidth: 36, textAlign: 'right' }}>{maxRating} stars</Typography>
        </Box>
        <Slider
          value={currentRating}
          onChange={(_, value) => setFilters(prev => ({ ...prev, rating: value }))}
          min={minRating}
          max={maxRating}
          step={0.5}
          valueLabelDisplay="off"
          sx={{ mt: 0, mb: 0.5 }}
        />
      </Box>

      <Typography gutterBottom sx={{ fontFamily: `'Inter', 'Roboto', 'Arial', sans-serif` }}>Stores</Typography>
      <FormGroup>
        {stores.map(store => (
          <FormControlLabel
            key={store}
            control={
              <Checkbox
                checked={filters.stores.includes(store)}
                onChange={() => {
                  const newStores = filters.stores.includes(store)
                    ? filters.stores.filter(s => s !== store)
                    : [...filters.stores, store];
                  setFilters(prev => ({ ...prev, stores: newStores }));
                }}
              />
            }
            label={<span style={{ fontFamily: 'Inter, Roboto, Arial, sans-serif' }}>{store}</span>}
          />
        ))}
      </FormGroup>
    </Box>
  );
}

export default FilterSidebar;
